import { Boxes, Clock, Database, Flag, GitBranch, LucideIcon, MessageSquare, Pause, PhoneCall, Play, Shield, ShieldOff, Moon, CalendarClock, UserX, Circle, CheckCircle2, XCircle } from "lucide-react";

export type CategoryVisual = {
    icon: LucideIcon;
    /** The family's name where blocks are listed by family (the palette), in plain words. */
    title: string;
    /**
     * Accent classes for the node card: border, header background, icon colour. Categorical
     * only (they tell block families apart, never a status), in the `-100` / `-800` tone scale.
     */
    border: string;
    header: string;
    iconColor: string;
};

/**
 * The only visual mapping the frontend owns. It is keyed by `category`, which the
 * server sends with every node, and it always resolves: an unknown category — a
 * handler registered after this file was written — renders grey and usable rather
 * than blank. That is the whole point, and it is under test.
 */
export const DEFAULT_CATEGORY_VISUAL: CategoryVisual = {
    icon: Boxes,
    title: "Other blocks",
    border: "border-gray-300",
    header: "bg-gray-100",
    iconColor: "text-gray-700"
};

/** In the order an author builds a workflow: start, reach out, wait, decide, record, end. */
const CATEGORY_VISUALS: Record<string, CategoryVisual> = {
    entry: { icon: Play, title: "Start", border: "border-teal-300", header: "bg-teal-100", iconColor: "text-teal-800" },
    call: { icon: PhoneCall, title: "Calls", border: "border-blue-300", header: "bg-blue-100", iconColor: "text-blue-800" },
    // Sky keeps SMS/email readable next to call's deeper blue.
    messaging: { icon: MessageSquare, title: "Messages", border: "border-sky-300", header: "bg-sky-100", iconColor: "text-sky-800" },
    flow: { icon: Clock, title: "Wait and hand over", border: "border-orange-300", header: "bg-orange-100", iconColor: "text-orange-800" },
    logic: { icon: GitBranch, title: "Decide and remember", border: "border-violet-300", header: "bg-violet-100", iconColor: "text-violet-800" },
    crm: { icon: Database, title: "CRM", border: "border-indigo-300", header: "bg-indigo-100", iconColor: "text-indigo-800" },
    // A flag rather than a cross: an exit is where a run finishes, won as often as lost.
    exit: { icon: Flag, title: "End", border: "border-pink-300", header: "bg-pink-100", iconColor: "text-pink-800" }
};

/** Older fixtures / casual names → the canonical category key above. */
const CATEGORY_ALIASES: Record<string, string> = {
    terminal: "exit",
    message: "messaging"
};

function canonicalCategory(category: string): string {
    return CATEGORY_ALIASES[category] ?? category;
}

export function categoryVisual(category: string | null | undefined): CategoryVisual {
    if (!category) return DEFAULT_CATEGORY_VISUAL;
    return CATEGORY_VISUALS[canonicalCategory(category)] ?? DEFAULT_CATEGORY_VISUAL;
}

/** A family's name: its own title, or the category itself, capitalised, when it is new. */
export function categoryTitle(category: string | null | undefined): string {
    if (!category) return DEFAULT_CATEGORY_VISUAL.title;
    const known = CATEGORY_VISUALS[canonicalCategory(category)];
    if (known) return known.title;
    const words = category.replace(/[_.-]+/g, " ").trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : DEFAULT_CATEGORY_VISUAL.title;
}

/** Where a family sorts: known ones in building order, new ones after them, by name. */
export function categoryRank(category: string | null | undefined): number {
    const index = category ? Object.keys(CATEGORY_VISUALS).indexOf(canonicalCategory(category)) : -1;
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/** Exposed so a test can assert the table stays a table and never a `switch`. */
export function knownCategories(): string[] {
    return Object.keys(CATEGORY_VISUALS);
}

/**
 * Behaviour badges. Keys come from `node.behaviors`, which the server derives from
 * `NodeBehavior`, so a behaviour added later shows up as a neutral dot with its
 * server-provided label instead of disappearing.
 */
export const DEFAULT_BEHAVIOR_ICON: LucideIcon = Circle;

const BEHAVIOR_ICONS: Record<string, LucideIcon> = {
    anti_spam: ShieldOff,
    weekend: Moon,
    calendar: CalendarClock,
    guard: Shield,
    assignee: UserX
};

export function behaviorIcon(key: string | null | undefined): LucideIcon {
    if (!key) return DEFAULT_BEHAVIOR_ICON;
    return BEHAVIOR_ICONS[key] ?? DEFAULT_BEHAVIOR_ICON;
}

export function knownBehaviorKeys(): string[] {
    return Object.keys(BEHAVIOR_ICONS);
}

/** Card state pictogram used on the highlighted node of a card's graph. */
export const DEFAULT_CARD_STATE_ICON: LucideIcon = Circle;

const CARD_STATE_ICONS: Record<string, LucideIcon> = {
    ready: Play,
    awaiting_provider: PhoneCall,
    awaiting_event: Clock,
    paused: Pause,
    completed: CheckCircle2,
    failed: XCircle
};

export function cardStateIcon(state: string | null | undefined): LucideIcon {
    if (!state) return DEFAULT_CARD_STATE_ICON;
    return CARD_STATE_ICONS[state] ?? DEFAULT_CARD_STATE_ICON;
}
